<template>
  <div class="mc-video-history-new__timeline-bar-wrapper">
    <div
      ref="barChannelsScroller"
      class="mc-video-history-new__bar-channels-scroller"
      :class="{ 'is-dragging': isDraggingMarker }"
      @wheel.prevent="
        handleTimelineZoom(
          $event,
          $refs.barChannelsScroller,
          $refs.barChannelsWrapper,
          periodInSeconds
        )
      "
      @scroll.passive="handleTimelineViewportChange"
    >
      <div ref="barChannelsWrapper" :style="{ width: `${zoomPercentage}%`, position: 'relative' }">
        <Timeline
          :startTime="period[0]"
          :endTime="period[1]"
          :markerPosition="displayedMarkerPosition"
          :filelistUnion="filelistUnion"
          :countTimeLabel="countTimeLabel"
          :zoomPercentage="zoomPercentage"
          :isLoading="loadingFilelist"
          :timelineDisabled="videoChannelActive.length === 0"
          @clickTimelineDatetime="handleClickTimelineDatetime"
          @markerDragStart="handleMarkerDragStart"
        />
        <div
          v-show="!loadingFilelist"
          class="mc-video-history-new__marker-line"
          :class="{ dragging: isDraggingMarker }"
          :style="{ left: `${displayedMarkerPosition}%` }"
          @pointerdown.prevent="handleMarkerDragStart"
        >
          <i class="icon-fill_play triangle-top" />
          <div class="line"></div>
          <i class="icon-fill_play triangle-bottom" />
        </div>
        <div class="mc-video-history-new__bar-channels-wrapper">
          <BarChannel
            v-for="ch in statusText.split(',')"
            :key="Number(ch)"
            :channel="deviceTypeActive === 'jc' ? Number(ch) + 1 : Number(ch)"
            :device-type-active="deviceTypeActive"
            :is-channel-active="
              videoChannelActive.includes(deviceTypeActive === 'jc' ? Number(ch) + 1 : Number(ch))
            "
            :timeline-width="timelineWidth"
            :viewport-start="timelineViewportStart"
            :viewport-width="timelineViewportWidth"
            :start-time-buffered="
              bufferedTimeState(deviceTypeActive === 'jc' ? Number(ch) + 1 : Number(ch))?.start ??
              ''
            "
            :end-time-buffered="
              bufferedTimeState(deviceTypeActive === 'jc' ? Number(ch) + 1 : Number(ch))?.end ?? ''
            "
            :single-click-delay-ms="barSingleClickDelayMs"
            @clickBarDatetime="handleClickBarDatetime"
            @doubleClickBarDatetime="handleDoubleClickBarDatetime"
          />
        </div>
      </div>
    </div>
    <portal-target name="video-history-bar-tooltips" multiple slim />
  </div>
</template>

<script>
import BarChannel from './BarChannel.vue'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import Timeline from '../Timeline.vue'
import { Helper } from '@/mixin/helper'
import ZoomTimelineMixin from '../../../mixins/zoom-timeline.js'
import { calculateTimelineRangeFit } from '../../../helper/timeline-range-fit'
moment.locale('id')

export default {
  components: {
    BarChannel,
    Timeline,
  },
  mixins: [ZoomTimelineMixin, Helper],
  props: {
    statusText: {
      type: String,
      required: false,
      default: '',
    },
    deviceTypeActive: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['intentTimeChanges'],

  data() {
    return {
      isDraggingMarker: false,
      hasPendingMarkerChange: false,
      draftMarkerPosition: null,
      markerDragDebounce: null,
      markerDragDebounceMs: 300,
      suppressTimelineClick: false,
      timelineWidth: 0,
      timelineViewportStart: 0,
      timelineViewportWidth: 0,
      timelineViewportResizeObserver: null,
      initialFilelistPaddingPx: 5,
      hasAppliedInitialFilelistFit: false,
      barSingleClickDelayMs: 200,
    }
  },

  computed: {
    ...mapState('videoHistory', [
      'videoChannelActive',
      'period',
      'filelistUnion',
      'loadingFilelist',
      'mapWebviewDatetimePerChannel',
      'markerPositionTimeline',
      'mapWebviewBufferedTimePerChannel',
      'vehicleStatuses',
      'vehicleIdActive',
      'mapFilelist',
    ]),
    ...mapGetters('videoHistory', [
      'isThereIsWebviewActive',
      'periodInSeconds',
      'deviceTimezone',
      'markerTimelineDate',
    ]),
    displayedMarkerPosition() {
      return this.draftMarkerPosition === null
        ? this.markerPositionTimeline
        : this.draftMarkerPosition
    },
    bufferedTimeState() {
      return (channel) => this.mapWebviewBufferedTimePerChannel[channel]
    },
    imei() {
      return this.vehicleStatuses[this.vehicleIdActive]?.vehicle?.imei ?? ''
    },
  },

  watch: {
    loadingFilelist: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.hasAppliedInitialFilelistFit = false
          return
        }

        this.$nextTick(this.fitInitialFilelistRange)
      },
    },
    zoomPercentage() {
      this.$nextTick(this.handleTimelineViewportChange)
    },
    mapWebviewDatetimePerChannel: {
      handler() {
        if (this.isDraggingMarker || this.hasPendingMarkerChange) {
          return
        }

        let newestDatetime = null
        let newestTimestamp = -Infinity

        Object.values(this.mapWebviewDatetimePerChannel).forEach((datetime) => {
          const timestamp = moment.utc(datetime).valueOf()

          if (Number.isFinite(timestamp) && timestamp > newestTimestamp) {
            newestTimestamp = timestamp
            newestDatetime = datetime
          }
        })

        if (newestDatetime) {
          this.goToMarkerPosition(newestDatetime)
          if (!this.isZoomDebouncing) {
            this._zoomToMaxAtPosition()
          }
        }
      },
      immediate: true,
      deep: true,
    },
    '$store.state.videoHistory.hasIntentToTimeChange'(hasIntentToTimeChange) {
      if (!hasIntentToTimeChange) return
      if (this.isZoomDebouncing) return

      this._zoomToMaxAtPosition()
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.handleTimelineViewportChange()
      this.fitInitialFilelistRange()

      if (typeof ResizeObserver === 'undefined') return

      if (!this.$refs.barChannelsScroller || !this.$refs.barChannelsWrapper) return

      this.timelineViewportResizeObserver = new ResizeObserver(this.handleTimelineViewportChange)
      this.timelineViewportResizeObserver.observe(this.$refs.barChannelsScroller)
      this.timelineViewportResizeObserver.observe(this.$refs.barChannelsWrapper)
    })
  },

  beforeDestroy() {
    window.clearTimeout(this.markerDragDebounce)
    this.removeMarkerDragListeners()
    this.timelineViewportResizeObserver?.disconnect()
  },

  methods: {
    getFilelistUnionUtcRange() {
      let earliestStart = null
      let latestEnd = null

      this.filelistUnion.forEach(({ startTime, endTime }) => {
        const start = this.getUtcDateFromLocalDate(startTime, this.deviceTimezone)
        const end = this.getUtcDateFromLocalDate(endTime, this.deviceTimezone)
        const startTimestamp = new Date(start).getTime()
        const endTimestamp = new Date(end).getTime()

        if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp)) return

        if (earliestStart === null || startTimestamp < earliestStart.timestamp) {
          earliestStart = { value: start, timestamp: startTimestamp }
        }

        if (latestEnd === null || endTimestamp > latestEnd.timestamp) {
          latestEnd = { value: end, timestamp: endTimestamp }
        }
      })

      if (!earliestStart || !latestEnd || earliestStart.timestamp >= latestEnd.timestamp) {
        return null
      }

      return { start: earliestStart.value, end: latestEnd.value }
    },
    fitInitialFilelistRange() {
      const scroller = this.$refs.barChannelsScroller
      const wrapper = this.$refs.barChannelsWrapper
      const range = this.getFilelistUnionUtcRange()

      if (!scroller || !wrapper || !scroller.clientWidth || !range) return

      const maximumZoom = this.calculateMaxZoomPercentage(
        this.periodInSeconds,
        scroller.clientWidth
      )
      const fit = calculateTimelineRangeFit({
        periodStart: this.period[0],
        periodEnd: this.period[1],
        rangeStart: range.start,
        rangeEnd: range.end,
        viewportWidth: scroller.clientWidth,
        horizontalPaddingPx: this.initialFilelistPaddingPx,
        minZoomPercentage: this.minZoomPercentage,
        maxZoomPercentage: maximumZoom,
      })

      if (!fit) return

      this.maxZoomPercentage = maximumZoom
      this.zoomPercentage = fit.zoomPercentage
      this.hasAppliedInitialFilelistFit = true

      this.$nextTick(() => {
        const targetScrollLeft =
          wrapper.scrollWidth * (fit.scrollStartPercentage / 100) - fit.horizontalPaddingPx
        const maximumScrollLeft = Math.max(wrapper.scrollWidth - scroller.clientWidth, 0)

        scroller.scrollLeft = Math.min(Math.max(targetScrollLeft, 0), maximumScrollLeft)
        this.handleTimelineViewportChange()
      })
    },
    handleTimelineViewportChange() {
      const scroller = this.$refs.barChannelsScroller
      const wrapper = this.$refs.barChannelsWrapper

      if (!scroller || !wrapper) return

      this.timelineWidth = wrapper.scrollWidth
      this.timelineViewportStart = scroller.scrollLeft
      this.timelineViewportWidth = scroller.clientWidth
    },
    _zoomToMaxAtPosition() {
      this.zoomToMaxAtPosition(
        this.$refs.barChannelsScroller,
        this.$refs.barChannelsWrapper,
        this.periodInSeconds,
        this.markerPositionTimeline
      )
    },
    goToMarkerPosition(datetime) {
      const [periodStart, periodEnd] = this.period

      if (!datetime || !periodStart || !periodEnd) return

      const startTimestamp = moment.utc(periodStart).valueOf()
      const endTimestamp = moment.utc(periodEnd).valueOf()
      const selectedTimestamp = moment.utc(datetime).valueOf()
      const duration = endTimestamp - startTimestamp

      if (
        !Number.isFinite(startTimestamp) ||
        !Number.isFinite(endTimestamp) ||
        !Number.isFinite(selectedTimestamp) ||
        duration <= 0
      ) {
        return
      }

      const position = ((selectedTimestamp - startTimestamp) / duration) * 100

      this.$store.commit(
        'videoHistory/setMarkerPositionTimeline',
        Math.min(Math.max(position, 0), 100)
      )
    },

    async handleTimeChanged(datetime) {
      this.goToMarkerPosition(datetime)

      const targetChannelBuffered = []

      this.videoChannelActive.forEach((channel) => {
        const buffered = this.bufferedTimeState(channel)

        if (buffered && this.isBetweenDate(datetime, buffered.start, buffered.end)) {
          targetChannelBuffered.push(channel)
          if (this.isThereIsWebviewActive) {
            this.$store.dispatch('videoHistory/postToStreamIframe', {
              imei: this.imei,
              channel,
              payload: {
                type: 'VIDEO_HISTORY_SEEK',
                data: {
                  channel,
                  imei: this.imei,
                  dateTime: datetime,
                },
              },
            })
          }
        }

        const filelist = this.mapFilelist[channel] ?? []

        const isThereFilelist = filelist.some((f) => {
          const utcStartTime = this.getUtcDateFromLocalDate(f.startTime, this.deviceTimezone)
          const utcEndTime = this.getUtcDateFromLocalDate(f.endTime, this.deviceTimezone)
          return (
            this.isBetweenDate(datetime, utcStartTime, utcEndTime) ||
            new Date(datetime).getTime() === new Date(utcStartTime).getTime()
          )
        })

        if (!isThereFilelist) {
          this.$store.commit('videoHistory/setMapWebviewStatePerChannel', {
            channel,
            state: null,
          })
        }
      })

      if (this.isThereIsWebviewActive && targetChannelBuffered.length === 0) {
        this.$emit('intentTimeChanges')
        await this.$nextTick()
        this.$store.commit('videoHistory/setHasIntentToTimeChange', true)
        this.$store.commit('videoHistory/resetMapWebviewBufferedTimePerChannel')
      }
    },

    async handleClickTimelineDatetime(datetime) {
      await this.handleTimeChanged(datetime)
    },
    async handleClickBarDatetime(datetime) {
      await this.handleTimeChanged(datetime)
    },

    async handleDoubleClickBarDatetime(datetime) {
      if (!datetime) return

      await this.handleTimeChanged(datetime)
      await this.$nextTick()

      this.videoChannelActive.forEach((channel) => {
        const filelist = this.mapFilelist[channel] ?? []

        const hasFilelist = filelist.some((file) => {
          const utcStart = this.getUtcDateFromLocalDate(file.startTime, this.deviceTimezone)
          const utcEnd = this.getUtcDateFromLocalDate(file.endTime, this.deviceTimezone)

          return (
            this.isBetweenDate(datetime, utcStart, utcEnd) ||
            new Date(datetime).getTime() === new Date(utcStart).getTime()
          )
        })

        if (hasFilelist) {
          this.$store.dispatch('videoHistory/playChannel', channel)
        }
      })
    },

    handleMarkerDragStart(event) {
      if (this.loadingFilelist || this.isDraggingMarker) return
      if (!event || !Number.isFinite(event.clientX)) return

      this.suppressTimelineClick = true
      this.isDraggingMarker = true
      this.hasPendingMarkerChange = true
      this.draftMarkerPosition = this.markerPositionTimeline

      window.addEventListener('pointermove', this.handleMarkerDragMove)
      window.addEventListener('pointerup', this.handleMarkerDragEnd)
      window.addEventListener('pointercancel', this.handleMarkerDragEnd)

      this.updateMarkerPositionFromPointer(event)
    },

    handleMarkerDragMove(event) {
      if (!this.isDraggingMarker) return

      this.updateMarkerPositionFromPointer(event)
    },

    handleMarkerDragEnd() {
      if (!this.isDraggingMarker) return

      this.isDraggingMarker = false
      this.removeMarkerDragListeners()

      // Pastikan posisi terakhir tetap diproses setelah pointer dilepas.
      this.scheduleMarkerPositionCommit()
    },

    updateMarkerPositionFromPointer(event) {
      const wrapper = this.$refs.barChannelsWrapper
      if (!wrapper) return

      const rect = wrapper.getBoundingClientRect()
      if (!rect.width) return

      const cursorX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)

      this.draftMarkerPosition = (cursorX / rect.width) * 100

      this.scheduleMarkerPositionCommit()
    },

    scheduleMarkerPositionCommit() {
      window.clearTimeout(this.markerDragDebounce)

      this.markerDragDebounce = window.setTimeout(() => {
        this.commitDraggedMarkerPosition()
      }, this.markerDragDebounceMs)
    },

    async commitDraggedMarkerPosition() {
      const position = this.draftMarkerPosition

      if (position === null) {
        this.hasPendingMarkerChange = false
        return
      }

      const datetime = this.getDatetimeFromMarkerPosition(position)

      if (!datetime) {
        this.draftMarkerPosition = null
        this.hasPendingMarkerChange = false
        return
      }

      await this.handleTimeChanged(datetime)

      this.draftMarkerPosition = null
      this.hasPendingMarkerChange = false
    },

    getDatetimeFromMarkerPosition(position) {
      const [periodStart, periodEnd] = this.period

      if (!periodStart || !periodEnd) return null

      const start = moment.utc(periodStart)
      const end = moment.utc(periodEnd)

      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return null
      }

      const normalizedPosition = Math.min(Math.max(Number(position), 0), 100)

      const durationMs = end.diff(start)

      return start
        .clone()
        .add(durationMs * (normalizedPosition / 100), 'milliseconds')
        .toISOString()
    },

    removeMarkerDragListeners() {
      window.removeEventListener('pointermove', this.handleMarkerDragMove)
      window.removeEventListener('pointerup', this.handleMarkerDragEnd)
      window.removeEventListener('pointercancel', this.handleMarkerDragEnd)
    },
  },
}
</script>
